import {
    SITE_BUILDER_COUNTDOWN_LABELS as sharedSiteBuilderCountdownLabels,
} from "./siteBuilderCountdownLabels.js";

export const SITE_BUILDER_COUNTDOWN_TICK_MS = 1_000;

export const SITE_BUILDER_COUNTDOWN_LABEL_KEYS = [
    "ui.countdown.days",
    "ui.countdown.hours",
    "ui.countdown.minutes",
    "ui.countdown.seconds",
] as const;

export const SITE_BUILDER_COUNTDOWN_LANGUAGE_CODES = [
    "cs",
    "sk",
    "en",
    "de",
    "es",
    "pl",
    "hu",
    "ro",
] as const;

export type SiteBuilderCountdownLabelKey = typeof SITE_BUILDER_COUNTDOWN_LABEL_KEYS[number];
export type SiteBuilderCountdownLanguageCode = typeof SITE_BUILDER_COUNTDOWN_LANGUAGE_CODES[number];

type SiteBuilderCountdownLocalizedLabel = Readonly<Record<SiteBuilderCountdownLanguageCode, string>>;

export const SITE_BUILDER_COUNTDOWN_LABELS = sharedSiteBuilderCountdownLabels satisfies Readonly<
    Record<SiteBuilderCountdownLabelKey, SiteBuilderCountdownLocalizedLabel>
>;

export type SiteBuilderCountdownItem = Readonly<{
    labelKey: SiteBuilderCountdownLabelKey;
    text: string;
    value: number;
}>;

export function siteBuilderCountdownValues(
    targetDate: string,
    now: number,
): [number, number, number, number] {
    const target = Date.parse(targetDate.trim().replace(" ", "T"));
    const remaining = Number.isFinite(target)
        ? Math.max(0, target - now)
        : 0;

    return [
        Math.floor(remaining / 86_400_000),
        Math.floor(remaining / 3_600_000) % 24,
        Math.floor(remaining / 60_000) % 60,
        Math.floor(remaining / 1_000) % 60,
    ];
}

function siteBuilderCountdownLanguage(
    languageCode: string | null | undefined,
): SiteBuilderCountdownLanguageCode {
    const normalized = (languageCode ?? "")
        .trim()
        .toLocaleLowerCase()
        .split(/[-_]/u, 1)[0];

    return SITE_BUILDER_COUNTDOWN_LANGUAGE_CODES.includes(
        normalized as SiteBuilderCountdownLanguageCode,
    )
        ? normalized as SiteBuilderCountdownLanguageCode
        : "en";
}

export function siteBuilderCountdownLabel(
    languageCode: string | null | undefined,
    labelKey: SiteBuilderCountdownLabelKey,
): string {
    return SITE_BUILDER_COUNTDOWN_LABELS[labelKey][
        siteBuilderCountdownLanguage(languageCode)
    ];
}

export function siteBuilderCountdownItems(
    targetDate: string,
    now: number,
): readonly SiteBuilderCountdownItem[] {
    const values = siteBuilderCountdownValues(targetDate, now);

    return SITE_BUILDER_COUNTDOWN_LABEL_KEYS.map((labelKey, index) => {
        const value = values[index] ?? 0;

        return {
            labelKey,
            text: String(value).padStart(2, "0"),
            value,
        };
    });
}
